import React, { RefObject, useEffect, useRef } from "react";
import { useChatContext } from "@containers/chat";
import { useActor } from "@xstate/react";
import { BOTTOM_SCROLL_THRESHOLD } from "@constants";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown } from "@fortawesome/pro-solid-svg-icons";

type IMessageBoxInnerProps = {
  wrapRef: RefObject<HTMLDivElement>;
  children: React.ReactNode;
};

// Distance from the true bottom that still counts as "pinned to bottom".
// Kept small on purpose: anything larger re-pins users who scrolled up to read history.
const BOTTOM_PIN_THRESHOLD = 48;

export const MessageBoxInner = React.memo(
  ({ wrapRef, children }: IMessageBoxInnerProps) => {
    const machineService = useChatContext();
    const [state] = useActor(machineService.chatService);
    const innerRef = useRef(null);
    const isPinnedToBottom = useRef(true);
    const [showGoToBottom, setShowGoToBottom] = React.useState(false);

    const stateRef = useRef(state);
    stateRef.current = state;

    // Re-pin to the bottom on ANY content resize, not only when a message first
    // renders. Image messages render a fixed-size placeholder and swap in the real
    // image once it has downloaded, which changes the content height after the
    // initial scroll — so the latest message (and any trailing option menu below
    // the images) would otherwise end up below the fold.
    useEffect(() => {
      const inner = innerRef.current;
      if (!inner) return;

      const observer = new ResizeObserver(() => {
        if (!wrapRef.current) return;

        const currentState = stateRef.current;
        const shouldForceScroll =
          currentState.matches("firstFetchMessageSuccess") ||
          currentState.matches("sendingMessage") ||
          currentState.matches("uploadingMessage");

        if (!shouldForceScroll && !isPinnedToBottom.current) return;

        // Use requestAnimationFrame to wait for the next animation frame
        requestAnimationFrame(() => {
          if (!wrapRef.current) return;
          // Scroll to the bottom
          wrapRef.current.scrollTop = wrapRef.current.scrollHeight;
          // Pre-set so the scroll event this triggers cannot unpin us on a
          // subpixel comparison.
          isPinnedToBottom.current = true;
        });
      });

      observer.observe(inner);

      return () => {
        observer.disconnect();
      };
      // `state` is read through stateRef so the observer is not torn down and
      // re-observed (which re-fires it) on every machine transition.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const currentScrollRef = wrapRef.current;
      if (!currentScrollRef) return;

      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = currentScrollRef;

        // At the true bottom scrollTop === scrollHeight - clientHeight, so the
        // distance has to account for clientHeight.
        isPinnedToBottom.current =
          scrollHeight - scrollTop - clientHeight <= BOTTOM_PIN_THRESHOLD;

        setShowGoToBottom(scrollHeight - scrollTop > BOTTOM_SCROLL_THRESHOLD);
      };

      currentScrollRef.addEventListener("scroll", handleScroll, {
        passive: true,
      });

      return () => {
        currentScrollRef.removeEventListener("scroll", handleScroll);
      };
    }, []);
    return (
      <div ref={innerRef} className="flex-1">
        {children}
        <AnimatePresence>
          {showGoToBottom && (
            <motion.button
              onClick={() => {
                if (wrapRef.current) {
                  wrapRef.current.scrollTop = wrapRef.current.scrollHeight;
                }
              }}
              className="fixed flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[white] shadow-md"
              style={{
                left: "50%",
                transform: "translateX(-50%)",
              }}
              initial={{ opacity: 0, bottom: -80, scale: 0.5 }}
              animate={{ opacity: 1, bottom: 80, scale: 1 }}
              exit={{ opacity: 0, bottom: -80, scale: 1 }}
              transition={{
                type: "spring",
                bounce: 0.35,
                duration: 1,
              }}
            >
              <FontAwesomeIcon className="text-[black]" icon={faArrowDown} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    );
  }
);
